import { useState } from 'react';
import {
  Box, Typography, TextField, Button, CircularProgress, Alert, Paper, Chip, Link,
} from '@mui/material';
import { TravelExplore as TraceIcon } from '@mui/icons-material';
import api from '../../config/api';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useStore from '../../config/store';
import formatRut from '../../erp/formatRut';

/**
 * Trazabilidad end-to-end por RUT (criterio 14 de la spec): desde el home
 * ERP, buscar un trabajador y recorrer su cadena cliente → cotización →
 * ficha → contrato con links directos.
 */
const ErpTraceabilitySearch = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { organization, authToken } = useStore.getState();

  const [rut, setRut] = useState('');
  const [chains, setChains] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSearch = async () => {
    if (!rut.trim()) return;
    try {
      setLoading(true);
      setError(null);
      setChains(null);
      const response = await api.get('/erp/traceability', {
        params: { organizationId: organization, workerRut: rut.trim() },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (response.data.response?.status_response === 'OK') {
        setChains(response.data.response.data || []);
      }
    } catch (err) {
      console.error(err);
      setError(t('erp.traceability.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
        {t('erp.traceability.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('erp.traceability.subtitle')}
      </Typography>

      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <TextField size="small" placeholder={t('erp.traceability.placeholder')}
          value={rut} onChange={(e) => setRut(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          sx={{ maxWidth: 280 }} />
        <Button variant="outlined" startIcon={loading ? <CircularProgress size={16} /> : <TraceIcon />}
          disabled={loading || !rut.trim()} onClick={handleSearch} sx={{ textTransform: 'none' }}>
          {t('erp.traceability.search')}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {chains !== null && chains.length === 0 && (
        <Alert severity="info">{t('erp.traceability.empty')}</Alert>
      )}

      {(chains || []).map((chain) => (
        <Paper key={chain.contract.uid} elevation={0}
          sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 2, mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
            <Typography variant="subtitle2" fontWeight={700}>
              {chain.contract.workerName || formatRut(chain.contract.workerRut)}
            </Typography>
            <Chip size="small"
              label={t(`erp.contracts.exportStatus.${chain.contract.exportStatus}`)}
              color={chain.contract.exportStatus === 'exported' ? 'success'
                : chain.contract.exportStatus === 'pending' ? 'warning' : 'error'} />
          </Box>
          <Typography variant="body2" sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
            <Link component="button" underline="hover"
              onClick={() => navigate(`/erp/clients/${chain.client?.uid}`)}>
              {chain.client?.legalName || t('erp.clients.title')}
            </Link>
            {' → '}
            <Link component="button" underline="hover"
              onClick={() => navigate(`/erp/quotations/${chain.quotation?.uid}`)}>
              {chain.quotation ? `${chain.quotation.quotationNumber || t('erp.quotations.title')}: ${chain.quotation.jobTitleName}` : '—'}
            </Link>
            {' → '}
            <Link component="button" underline="hover"
              onClick={() => navigate(`/erp/employees/${chain.employeeRecord?.uid}`)}>
              {t('erp.employees.title')}
            </Link>
            {' → '}
            <Link component="button" underline="hover"
              onClick={() => navigate(`/erp/contracts/${chain.contract.uid}`)}>
              {t('erp.contracts.title')} ({chain.contract.dates?.startDate})
            </Link>
          </Typography>
        </Paper>
      ))}
    </Box>
  );
};

export default ErpTraceabilitySearch;
